import { UserNotificationResponseDto } from './../../shared/models/responses/user-notification.response';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Profile } from '../../shared/models/profile';
import { environment } from '../../../environments/environment';
import { tap, map, catchError } from 'rxjs/operators';
import { CandidateStore } from '../store/candidate-store';
import { UiStateStore } from '../../shared/store/ui-state-store.service';
import { Resume } from '../../shared/models/resume';
import { BusinessDetails } from '../models/business-details';
import { EmployerStore } from '../store/employer-store';
import { JobPost } from '../../jobs/models/job-post';
import { JobsStore } from '../../jobs/store/jobs-store.service';
import { JobPreview } from '../models/job-preview';
import { CandidateSummary } from '../../candidates/models/candidate-summary';
import { SearchResult } from '../../shared/models/search-result';
import { ReportRequestDto } from 'src/app/shared/models/requests/report.request';
import { EntityTypes } from 'src/app/shared/enums/entity-types';

@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(private httpClient: HttpClient,
    public candidateStore: CandidateStore,
    public employerStore: EmployerStore,
    public jobsStore: JobsStore,
    public uiState: UiStateStore) { }

  public getProfile(accountId: string): Observable<Profile> {
    const url = `${environment.baseApiUrl}/profiles/getByAccountId/${accountId}`;
    return this.httpClient.get<Profile>(url)
      .pipe(map(response => {
        return Profile.parse(response);
      }),
        tap(response => {
          this.candidateStore.setProfile(response);
        }),
        catchError(err => {
          this.uiState.setError(`Error when requesting profile: ${err["message"]}`);
          return throwError(err);
        })
      );
  }

  public saveProfile(profile: Profile): Observable<Profile> {
    const url = `${environment.baseApiUrl}/profiles`;
    return this.httpClient.post<Profile>(url, profile).pipe(map(response => {
      return Profile.parse(response);
    }), tap(response => {
      this.candidateStore.setProfile(response);
      this.uiState.setSuccess(`Your profile information is successfully saved.`);
    }),
      catchError(err => {
        this.uiState.setError(`Error when saving profile: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  public saveResume(resume: Resume): Observable<Resume> {
    const url = `${environment.baseApiUrl}/resume`;
    return this.httpClient.post<Resume>(url, resume).pipe(map(response => {
      return response;
    }), tap(response => {
      this.uiState.setSuccess(`Your resume is successfully saved.`);
    }),
      catchError(err => {
        this.uiState.setError(`Error when saving resume: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  public getResumeByAccountId(accountId: string): Observable<Resume> {
    const url = `${environment.baseApiUrl}/resume/account/${accountId}`;
    return this.httpClient.get<Resume>(url).pipe(map(response => {
      return Resume.parse(response);
    }),
      catchError(err => {
        this.uiState.setError(`Error when getting Resume: ${err["message"]}`);
        return throwError(err);
      }));
  }

  public saveBusinessDetails(businessDetails: BusinessDetails): Observable<BusinessDetails> {
    const url = `${environment.baseApiUrl}/business`;
    return this.httpClient.post<BusinessDetails>(url, businessDetails).pipe(map(response => {
      return BusinessDetails.parse(response);
    }), tap(response => {
      this.employerStore.setBusinessDetails(response);
      this.uiState.setSuccess(`Your business details are successfully saved.`);
    }),
      catchError(err => {
        this.uiState.setError(`Error when saving business details: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  public getBusinessDetails(accountId: string): Observable<BusinessDetails> {
    const url = `${environment.baseApiUrl}/business/${accountId}`;
    return this.httpClient.get<BusinessDetails>(url)
      .pipe(map(response => {
        return BusinessDetails.parse(response);
      }),
        tap(response => {
          this.employerStore.setBusinessDetails(response);
        }),
        catchError(err => {
          this.uiState.setError(`Error when getting Business Details: ${err["message"]}`);
          return throwError(err);
        }));
  }

  public saveJobPost(jobPost: JobPost): Observable<JobPost> {
    const url = `${environment.baseApiUrl}/jobs`;
    return this.httpClient.post<JobPost>(url, jobPost).pipe(map(response => {
      return JobPost.parse(response);
    }), tap(response => {
      this.employerStore.setJobPost(response);
      this.uiState.setSuccess(`JobPost is successfully saved.`);
    }),
      catchError(err => {
        this.uiState.setError(`Error when saving JobPost: ${err["message"]}`);
        return throwError(err);
      })
    );
  }

  public getJobPosts(accountId: string): Observable<JobPost[]> {
    const url = `${environment.baseApiUrl}/jobs/${accountId}`;
    return this.httpClient.get<JobPost[]>(url)
      .pipe(map(response => {
        return response.map(resp => JobPost.parse(resp));
      }),
        tap(response => {
          let jobPosts = response.map(resp => JobPost.parse(resp));
          this.jobsStore.setJobPosts(jobPosts);
        }),
        catchError(err => {
          this.uiState.setError(`Error when getting Jobs: ${err["message"]}`);
          return throwError(err);
        }));
  }

  public getJobPreview(jobId: string): Observable<JobPreview> {
    const url = `${environment.baseApiUrl}/jobs/job-preview/${jobId}`;
    return this.httpClient.get<JobPreview>(url)
      .pipe(map(response => {
        return JobPreview.parse(response);
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting JobPreview: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobPost(jobId: string): Observable<JobPost> {
    const url = `${environment.baseApiUrl}/jobs/job/${jobId}`;
    return this.httpClient.get<JobPost>(url)
      .pipe(map(response => {
        let jobPost = JobPost.parse(response);
        this.employerStore.setJobPost(jobPost);
        return jobPost;
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting job post: ${err["message"]}`);
          return throwError(err);
        }));
  }

  deleteJobPost(jobId: string): Observable<any> {
    const url = `${environment.baseApiUrl}/jobs/${jobId}`;
    return this.httpClient.delete(url);
  }

  getBookmarkedCandidates(accountId: string, pageNumber: number): Observable<SearchResult<CandidateSummary>> {
    const url = `${environment.baseApiUrl}/candidates/shortlist/${accountId}/${pageNumber}`;
    return this.httpClient.get<SearchResult<CandidateSummary>>(url)
      .pipe(map(response => {
        return SearchResult.parse<CandidateSummary>(response, new CandidateSummary());
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting bookmarked candidates: ${err["message"]}`);
          return throwError(err);
        }));
  }

  getJobInvites(accountId: string): Observable<JobPost[]> {
    const url = `${environment.baseApiUrl}/jobs/invites/${accountId}`;
    return this.httpClient.get<JobPost[]>(url)
      .pipe(map(response => {
        return response.map(resp => JobPost.parse(resp));
      }),
        tap(response => {
          let jobPosts = response.map(resp => JobPost.parse(resp));
          this.jobsStore.setJobPosts(jobPosts);
        }),
        catchError(err => {
          this.uiState.setError(`Error when getting Jobs: ${err["message"]}`);
          return throwError(err);
        }));
  }

  declineJobInvite(jobPostId: any, candidateAccountId: string): Observable<void> {
    const url = `${environment.baseApiUrl}/candidates/${candidateAccountId}/decline/${jobPostId}`;
    return this.httpClient.get(url)
      .pipe(map(() => {
        return;
      }),
        catchError(err => {
          this.uiState.setError(`Error when getting Jobs: ${err["message"]}`);
          return throwError(err);
        }));
  }

  public getUserNotifications(accountId: string): Observable<UserNotificationResponseDto[]> {
    const url = `${environment.baseApiUrl}/notification/${accountId}`;
    return this.httpClient.get<UserNotificationResponseDto[]>(url)
    .pipe(map(response => {
      return response as UserNotificationResponseDto[];
    }),
      catchError(err => {
        this.uiState.setError(`Error when fetching user notifications: ${err["message"]}`);
        return throwError(err);
      }));
  }

  markNotificationRead(notificationId: string): Observable<boolean> {
    const url = `${environment.baseApiUrl}/notification/${notificationId}/mark-as-read`;
    return this.httpClient.get<boolean>(url)
    .pipe(map(response => {
      return response as boolean;
    }),
      catchError(err => {
        this.uiState.setError(`Error when updating notification: ${err["message"]}`);
        return throwError(err);
      }));
  }

  public reportAsMalicious(request: ReportRequestDto): Observable<boolean> {
    const type = request.entityType == EntityTypes.job ? "jobs" : "resume";
    const url = `${environment.baseApiUrl}/${type}/report`;
    return this.httpClient.post<boolean>(url, request)
    .pipe(
      catchError(err => {
        this.uiState.setError(`Error when reporting: ${err["message"]}`);
        return throwError(err);
      })
    );
  }
}
